import {
  Button,
  Checkbox,
  EmailInput,
  PhoneInput,
  Text,
  isValidEmail,
  isValidPhoneNumber,
} from '@restworld/ui-ds-base';
import { memo } from 'react';
import { FacebookButton } from '../common/facebook-button';
import { GoogleButton } from '../common/google-button';
import { Separator } from '../common/separator';
import { InformationIcon, config } from '@restworld/utils-common';
import { useSignupForm } from './hooks/use-signup-form';
import { AuthTitle, authPageTitleClassName } from '../common/auth-title';
import { AuthSubtitle, authPageSubtitleClassName } from '../common/auth-subtitle';
import { Controller } from 'react-hook-form';
import { useSignupUtils } from './hooks/use-signup-utils';
import { HeaderWrapper } from '../common/head-wrapper';
import { useTranslation } from 'next-i18next';
import AlreadyInvitedCollaboratorModal from './already-invited-collaborator-modal';
import { cn } from '@restworld/utils-common';

export interface SignupFormProps {
  isModal?: boolean;
  activeUrl?: string;
}

export const SignupForm = memo(({ isModal = true, activeUrl }: SignupFormProps) => {
  const { nonExistingUserAccount, navigateToLogin, onClose } = useSignupUtils({ isModal });
  const { loading, onSubmit, handleSubmit, control, errors } = useSignupForm({
    currentUrl: activeUrl,
    isModal: isModal,
    email: nonExistingUserAccount.email,
  });
  const { t: commonT } = useTranslation('common');
  const { t } = useTranslation('auth-flow');

  return (
    <FormWrapper {...{ isModal }}>
      <StyledFormContainer {...{ isModal }}>
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 w-full">
            <Controller
              name="email"
              control={control}
              rules={{ required: t('signup_form_insert_valid_email'), validate: isValidEmail }}
              render={({ field }) => (
                <EmailInput
                  label={
                    <span>
                      {commonT('email')}
                      <span className="text-red-600">*</span>
                    </span>
                  }
                  onChange={field.onChange}
                  variant={errors['email'] ? 'error' : 'primary'}
                  supportText={errors['email']?.message}
                  value={field.value}
                />
              )}
            />
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ required: t('signup_form_insert_valid_phone_number'), validate: isValidPhoneNumber }}
              render={({ field }) => (
                <PhoneInput
                  label={
                    <span>
                      {commonT('phone_number')}
                      <span className="text-red-600">*</span>
                    </span>
                  }
                  onChange={field.onChange}
                  priorityCountries={['IT']}
                  menuDialogStyles={['min-w-full']}
                  variant={errors['phoneNumber'] ? 'error' : 'primary'}
                  supportText={errors['phoneNumber']?.message}
                />
              )}
            />

            <div className="flex flex-row items-start justify-start w-full gap-2">
              <InformationIcon />
              <p className={cn('text-gray-500 text-base', !isModal && 'text-sm text-neutral-700')}>
                {t('signup_form_description_text')}
              </p>
            </div>
          </div>
          <div className={cn('flex flex-col items-start gap-4', isModal ? 'mt-8' : 'mt-6')}>
            <Controller
              name="acceptedTerms"
              control={control}
              rules={{
                required: t('signup_form_accept_terms_text'),
                validate: (value) => value === true,
              }}
              render={({ field }) => {
                const isError = errors['acceptedTerms']?.message ? true : false;
                return (
                  <Checkbox
                    {...{
                      isChecked: field.value,
                      onChange: (event) => {
                        const checked = event.target.checked;
                        field.onChange(checked);
                      },
                      onClick: (_) => {
                        field.onChange(!field.value);
                      },
                      iserror: isError,
                      title: (
                        <Text
                          className={`text-gray-500 font-light leading-6 text-sm w-full inline-block${
                            isError ? ' text-red-500' : ''
                          }`}
                        >
                          {t('signup_form_view_terms_and_condition_text_1')}
                          <a
                            href={config.workerTermsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-800 cursor-pointer"
                          >
                            <StyledLink isError={isError}>
                              {t('signup_form_view_terms_and_condition_text_2')}
                            </StyledLink>
                          </a>
                          {t('signup_form_view_terms_and_condition_text_3')}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-800 cursor-pointer"
                          >
                            <StyledLink isError={isError}>
                              {t('signup_form_view_terms_and_condition_text_4')}
                            </StyledLink>
                          </a>
                        </Text>
                      ),
                    }}
                  />
                );
              }}
            />
            <Button fullwidth={true} type="submit" loading={loading}>
              {t('signup_form_signup_button_text')}
            </Button>
            {isModal && <Separator />}
            <div className="flex flex-col w-full gap-2">
              <GoogleButton {...{ isModal: true, closeModal: onClose }} />
              <FacebookButton {...{ isModal: true, closeModal: onClose }} />
            </div>
          </div>
        </form>
      </StyledFormContainer>
      {isModal ? (
        <div className="flex flex-row justify-center mt-6 gap-1.75 text-sm leading-5.25 font-medium">
          <p>{t('signup_form_login_redirect_text')}</p>
          <StyledLoginLink onClick={navigateToLogin}>{commonT('login')}</StyledLoginLink>
        </div>
      ) : (
        <div className="flex justify-center mt-4">
          <Button type="button" variant="ghost" onClick={navigateToLogin}>
            {t('signup_form_login_redirect_text')} {commonT('login')}
          </Button>
        </div>
      )}
      <AlreadyInvitedCollaboratorModal />
    </FormWrapper>
  );
});

const FormWrapper = ({
  isModal = true,
  className,
  ...props
}: { isModal?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full', isModal && 'mt-5', className)} {...props} />
);
const StyledFormContainer = ({
  isModal = true,
  className,
  ...props
}: { isModal?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col flex-1 w-full',
      isModal && 'box-border bg-primary-50 rounded-3.75 p-4 mt-4',
      className
    )}
    {...props}
  />
);

export const SignupHeader = ({ isModal = true }: { isModal?: boolean }) => {
  const { t } = useTranslation('auth-flow');
  const { nonExistingUserAccount } = useSignupUtils({ isModal });

  if (!isModal) {
    return (
      <HeaderWrapper className="items-center gap-4" isModal={isModal}>
        <AuthTitle className={authPageTitleClassName} data-cy="sign-up-form-email-not-found-heading">
          {nonExistingUserAccount.email ? t('signup_form_header_email_does_not_exist') : t('signup_form_header_page')}
        </AuthTitle>
        <AuthSubtitle className={authPageSubtitleClassName}>{t('signup_form_subheader_page_variant')}</AuthSubtitle>
      </HeaderWrapper>
    );
  }

  return (
    <HeaderWrapper className="gap-2 mt-2" isModal={isModal}>
      <AuthTitle isModal={isModal} data-cy="sign-up-form-email-not-found-heading">
        {nonExistingUserAccount.email
          ? t('signup_form_header_email_does_not_exist')
          : t('signup_form_header_modal_variant')}
      </AuthTitle>
      <AuthSubtitle isModal={isModal}>{t('signup_form_subheader_modal_variant')}</AuthSubtitle>
    </HeaderWrapper>
  );
};

export const StyledCheckbox = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <input
    className={cn(
      'w-4 h-4 text-primary-600 bg-gray-50 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600',
      className
    )}
    {...props}
  />
);

export const StyledLink = ({
  isError,
  className,
  ...props
}: { isError?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <span className={cn(isError ? 'text-red-500' : 'text-gray-500 underline underline-offset-4', className)} {...props} />
);

const StyledLoginLink = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-primary-900 cursor-pointer', className)} {...props} />
);
